import {
  MutationOptions,
  QueryObserverOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { toast } from 'react-toastify';

import {
  approveTicketApi,
  changeTicketClaimStatusApi,
  changeTicketDetailsApi,
  changeTicketPriorityApi,
  closeTicketApi,
  createScreenshotMessageApi,
  createTicketApi,
  createTicketAttachmentApi,
  createTicketMessageApi,
  createTicketMultiAttachmentApi,
  createTicketMultiPartAttachmentCompleteApi,
  createTicketMultiPartAttachmentInitiateApi,
  createTicketMultiPartAttachmentUploadApi,
  deleteTicketApi,
  exportTicketsApi,
  getTicketApi,
  getTicketAttachmentsApi,
  getTicketMessagesApi,
  getTicketsApi,
  getTicketsCountByRoofTopApi,
  markTicketsAsReadApi,
  markTicketsAsUnReadApi,
  reopenTicketApi,
} from 'api/tickets';
import {
  ApproveTicketRequestBodyDTO,
  ChangeTicketClaimStatusRequestBodyDTO,
  ChangeTicketDetailsRequestBodyDTO,
  ChangeTicketPriorityRequestBodyDTO,
  ChangeTicketsReadStatusRequestBodyDTO,
  CloseTicketRequestBodyDTO,
  CreateTicketAttachmentRequestBodyDTO,
  CreateTicketMessageRequestBodyDTO,
  CreateTicketMultiPartAttachmentCompleteRequestBodyDTO,
  CreateTicketMultiPartAttachmentInitiateRequestBodyDTO,
  CreateTicketMultiPartAttachmentInitiateResponseBodyDTO,
  CreateTicketMultiPartAttachmentUploadRequestBodyDTO,
  CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
  CreateTicketRequestBodyDTO,
  CreateTicketScreenshotMessageRequestBodyDTO,
  DeleteTicketRequestParamsDTO,
  ExportTicketsRequestDTO,
  GetTicketAttachmentsResponseDTO,
  GetTicketMessagesResponseDTO,
  GetTicketResponseDTO,
  GetTicketsCountByRooftopsParamsDTO,
  GetTicketsRequestParamsDTO,
  GetTicketsResponseDTO,
  ReopenTicketRequestBodyDTO,
} from 'declarations/dto/tickets';
import { RequestQueryKeysE, RequestStaleTimeE } from 'declarations/enums/react-query';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { ApiErrorI } from 'declarations/interfaces/errors';

type UseCreateTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, CreateTicketRequestBodyDTO>['onSuccess'];
};

type UseReopenTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ReopenTicketRequestBodyDTO>['onSuccess'];
};

type UseApproveTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ApproveTicketRequestBodyDTO>['onSuccess'];
};

type UseCloseTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, CloseTicketRequestBodyDTO>['onSuccess'];
};

type UseCreateTicketMessageParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<void, ApiErrorI, CreateTicketMessageRequestBodyDTO>['onSuccess'];
};

type UseCreateTicketScreenshotMessageParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<
    void,
    ApiErrorI,
    CreateTicketScreenshotMessageRequestBodyDTO
  >['onSuccess'];
};

type UseCreateTicketAttachmentParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO>['onSuccess'];
};

type UseCreateTicketMultiAttachmentParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO[]>['onSuccess'];
};

type UseCreateTicketMultiPartAttachmentInitiateParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<
    CreateTicketMultiPartAttachmentInitiateResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentInitiateRequestBodyDTO
  >['onSuccess'];
};

type UseCreateTicketMultiPartAttachmentUploadParamsT = {
  onSuccess: MutationOptions<
    CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentUploadRequestBodyDTO
  >['onSuccess'];
};

type UseCreateTicketMultiPartAttachmentCompleteParamsT = {
  ticketId: string;
  onSuccess: MutationOptions<
    void,
    ApiErrorI,
    CreateTicketMultiPartAttachmentCompleteRequestBodyDTO
  >['onSuccess'];
};

type UseFetchTicketAttachmentsParamsT = {
  ticketId: string;
  shouldFetch: boolean;
};

type UseFetchTicketMessagesParamsT = {
  ticketId: string;
};

type UseFetchTicketParamsT = {
  ticketId: string;
  onSuccess: QueryObserverOptions<GetTicketResponseDTO, ApiErrorI>['onSuccess'];
};

type UseChangeTicketReadStatusParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ChangeTicketsReadStatusRequestBodyDTO>['onSuccess'];
};

type UseChangeTicketClaimStatusParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ChangeTicketClaimStatusRequestBodyDTO>['onSuccess'];
};

type UseChangeTicketPriorityParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ChangeTicketPriorityRequestBodyDTO>['onSuccess'];
};

type UseDeleteTicketParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, DeleteTicketRequestParamsDTO>['onSuccess'];
};

type UseChangeTicketDetailsParamsT = {
  onSuccess: MutationOptions<void, ApiErrorI, ChangeTicketPriorityRequestBodyDTO>['onSuccess'];
};

export const useFetchTickets = (params: GetTicketsRequestParamsDTO) =>
  useInfiniteQuery<GetTicketsResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTickets, params],
    ({ pageParam = 1 }) =>
      getTicketsApi({ ...params, [TicketsRefinementKeysE.CurrentPage]: pageParam }),
    {
      getNextPageParam: (lastPage, pages) => {
        const maxCountOfPages = Math.ceil(
          lastPage.count / Number(params[TicketsRefinementKeysE.CountPerPage])
        );
        const nextPage = pages.length + 1;
        return nextPage <= maxCountOfPages ? nextPage : undefined;
      },
      staleTime: RequestStaleTimeE.Tickets,
    }
  );

export const useFetchTicket = ({ ticketId, onSuccess }: UseFetchTicketParamsT) =>
  useQuery<GetTicketResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTicket, ticketId],
    () => getTicketApi(ticketId),
    { enabled: !!ticketId, staleTime: RequestStaleTimeE.Ticket, onSuccess }
  );

export const useFetchTicketsCountByRoofTop = (params: GetTicketsCountByRooftopsParamsDTO) => {
  return useInfiniteQuery(
    [RequestQueryKeysE.FetchTicketsCountByRoofTop, params.roofTopIds],
    ({ pageParam = 1 }) =>
      getTicketsCountByRoofTopApi({
        ...params,
        paging: { currentPage: pageParam, countPerPage: params.paging.countPerPage },
      }),
    {
      getNextPageParam: (lastPage, pages) => {
        const maxCountOfPages = Math.ceil(
          lastPage?.totalRoofTopCount / Number(params.paging.countPerPage)
        );
        const nextPage = pages.length + 1;
        return nextPage <= maxCountOfPages ? nextPage : null;
      },
      staleTime: RequestStaleTimeE.TicketsCountByRoofTop,
    }
  );
};

export const useCreateTicket = ({ onSuccess }: UseCreateTicketParamsT) =>
  useMutation<void, ApiErrorI, CreateTicketRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicket],
    createTicketApi,
    { onSuccess }
  );

export const useReopenTicket = ({ onSuccess }: UseReopenTicketParamsT) =>
  useMutation<void, ApiErrorI, ReopenTicketRequestBodyDTO>(
    [RequestQueryKeysE.ReopenTicket],
    reopenTicketApi,
    { onSuccess }
  );

export const useApproveTicket = ({ onSuccess }: UseApproveTicketParamsT) =>
  useMutation<void, ApiErrorI, ApproveTicketRequestBodyDTO>(
    [RequestQueryKeysE.ApproveTicket],
    approveTicketApi,
    { onSuccess }
  );

export const useCloseTicket = ({ onSuccess }: UseCloseTicketParamsT) =>
  useMutation<void, ApiErrorI, CloseTicketRequestBodyDTO>(
    [RequestQueryKeysE.CloseTicket],
    closeTicketApi,
    { onSuccess }
  );

export const useCreateTicketMessage = ({ ticketId, onSuccess }: UseCreateTicketMessageParamsT) =>
  useMutation<void, ApiErrorI, CreateTicketMessageRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicketMessage, ticketId],
    data => createTicketMessageApi(ticketId, data),
    { onSuccess }
  );

export const useCreateTicketScreenshotMessage = ({
  ticketId,
  onSuccess,
}: UseCreateTicketScreenshotMessageParamsT) =>
  useMutation<void, ApiErrorI, CreateTicketScreenshotMessageRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicketMessage, ticketId],
    data => createScreenshotMessageApi(ticketId, data),
    { onSuccess }
  );

export const useCreateTicketAttachment = ({
  ticketId,
  onSuccess,
}: UseCreateTicketAttachmentParamsT) =>
  useMutation<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicketAttachment, ticketId],
    data => createTicketAttachmentApi(ticketId, data),
    { onSuccess }
  );

export const useCreateTicketMultiAttachment = ({
  ticketId,
  onSuccess,
}: UseCreateTicketMultiAttachmentParamsT) =>
  useMutation<void, ApiErrorI, CreateTicketAttachmentRequestBodyDTO[]>(
    [RequestQueryKeysE.CreateTicketMultiAttachment, ticketId],
    data => createTicketMultiAttachmentApi(ticketId, data),
    { onSuccess }
  );

export const useCreateTicketMultiPartAttachmentInitiate = ({
  ticketId,
  onSuccess,
}: UseCreateTicketMultiPartAttachmentInitiateParamsT) =>
  useMutation<
    CreateTicketMultiPartAttachmentInitiateResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentInitiateRequestBodyDTO
  >(
    [RequestQueryKeysE.CreateTicketMultiPartAttachmentInitiate, ticketId],
    data => createTicketMultiPartAttachmentInitiateApi(ticketId, data),
    { onSuccess }
  );

export const useCreateTicketMultiPartAttachmentUpload = ({
  onSuccess,
}: UseCreateTicketMultiPartAttachmentUploadParamsT) =>
  useMutation<
    CreateTicketMultiPartAttachmentUploadResponseBodyDTO,
    ApiErrorI,
    CreateTicketMultiPartAttachmentUploadRequestBodyDTO
  >(
    [RequestQueryKeysE.CreateTicketMultiPartAttachmentUpload],
    data => createTicketMultiPartAttachmentUploadApi(data),
    { onSuccess }
  );

export const useCreateTicketMultiPartAttachmentComplete = ({
  ticketId,
  onSuccess,
}: UseCreateTicketMultiPartAttachmentCompleteParamsT) =>
  useMutation<void, ApiErrorI, CreateTicketMultiPartAttachmentCompleteRequestBodyDTO>(
    [RequestQueryKeysE.CreateTicketMultiPartAttachmentComplete],
    data => createTicketMultiPartAttachmentCompleteApi(ticketId, data),
    { onSuccess }
  );

export const useFetchTicketAttachments = ({
  ticketId,
  shouldFetch,
}: UseFetchTicketAttachmentsParamsT) =>
  useQuery<GetTicketAttachmentsResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTicketAttachments, ticketId],
    () => getTicketAttachmentsApi(ticketId),
    { enabled: shouldFetch, staleTime: RequestStaleTimeE.TicketAttachments }
  );

export const useTicketMarkReadStatus = ({ onSuccess }: UseChangeTicketReadStatusParamsT) =>
  useMutation<void, ApiErrorI, ChangeTicketsReadStatusRequestBodyDTO>(
    [RequestQueryKeysE.ChangeTicketReadStatus],
    markTicketsAsReadApi,
    { onSuccess }
  );

export const useTicketMarkUnReadStatus = ({ onSuccess }: UseChangeTicketReadStatusParamsT) =>
  useMutation<void, ApiErrorI, ChangeTicketsReadStatusRequestBodyDTO>(
    [RequestQueryKeysE.ChangeTicketUnreadStatus],
    markTicketsAsUnReadApi,
    { onSuccess }
  );

export const useFetchTicketMessages = ({ ticketId }: UseFetchTicketMessagesParamsT) =>
  useQuery<GetTicketMessagesResponseDTO, ApiErrorI>(
    [RequestQueryKeysE.FetchTicketMessages, ticketId],
    () => getTicketMessagesApi(ticketId),
    { enabled: !!ticketId, staleTime: RequestStaleTimeE.TicketMessages }
  );

export const useChangeClaimStatus = ({ onSuccess }: UseChangeTicketClaimStatusParamsT) =>
  useMutation<void, ApiErrorI, ChangeTicketClaimStatusRequestBodyDTO>(
    [RequestQueryKeysE.ChangeTicketClaimStatus],
    changeTicketClaimStatusApi,
    { onSuccess }
  );

export const useChangePriority = ({ onSuccess }: UseChangeTicketPriorityParamsT) =>
  useMutation<void, ApiErrorI, ChangeTicketPriorityRequestBodyDTO>(
    [RequestQueryKeysE.ChangeTicketPriority],
    changeTicketPriorityApi,
    { onSuccess }
  );

export const useDeleteTicket = ({ onSuccess }: UseDeleteTicketParamsT) =>
  useMutation<void, ApiErrorI, DeleteTicketRequestParamsDTO>(
    [RequestQueryKeysE.DeleteTicket],
    ({ ticketId }) => deleteTicketApi(ticketId),
    { onSuccess }
  );

export const useChangeTicketDetails = ({ onSuccess }: UseChangeTicketDetailsParamsT) =>
  useMutation<void, ApiErrorI, ChangeTicketDetailsRequestBodyDTO>(
    [RequestQueryKeysE.ChirpTicketDetailsChange],
    changeTicketDetailsApi,
    { onSuccess }
  );

export const useExportTickets = (body: ExportTicketsRequestDTO, onSuccess: (data: Blob) => void) =>
  useQuery<Blob, ApiErrorI>([RequestQueryKeysE.ExportTickets, body], () => exportTicketsApi(body), {
    enabled: false,
    onSuccess,
    onError: error => {
      toast.error(`Export failed: Something went wrong during export. ${error.code}`);
    },
  });
